import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { OrderConfirmationWithCartComponent } from 'src/app/redeem/modal/order-confirmation-with-cart/order-confirmation-with-cart.component';
import { ApiService } from 'src/app/services/api-services/api.service';
import {
  setRefreshCart,
  setSpinner,
  updateCartAll,
} from 'src/app/store/cart/cart.actions';
import { selectCartDetails } from 'src/app/store/cart/cart.selectors';
import { selectOrgSettings } from 'src/app/store/auth/auth.selector';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import {
  checkPaymentEnabledForOrg,
  showAlertForCartItems,
  showError,
  showWarning,
} from 'src/app/utility/common-logic';
import { Cart, CartDetails } from 'src/app/utility/model/cart';
import { OrganizationSettings } from 'src/app/utility/model/organization';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-cart-header',
  templateUrl: './cart-header.component.html',
  styleUrls: ['./cart-header.component.scss'],
})
export class CartHeaderComponent
  extends AutoUnsubscribeComponent
  implements OnInit {
  @Input() cartCount: number;
  @Input() cartDetails: CartDetails;
  @Input() cartItems: Cart[];
  @Input() cartStatus: string;
  isShowCartPopup = false;
  cartEditable = true;
  orgSettings: OrganizationSettings;

  constructor(
    private translate: TranslateService,
    private api: ApiService,
    private toastr: ToastrService,
    public dialog: MatDialog,
    private router: Router,
    private renderer: Renderer2,
    private el: ElementRef,
    private store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectOrgSettings), takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.orgSettings = settings;
      });
    this.cartEditable = this.cartStatus == 'In process' ? false : true;

    this.store
      .pipe(select(selectCartDetails), takeUntil(this.destroy$))
      .subscribe((details: any) => {
        this.cartCount = details?.count || 0;
      });
  }

  get showAlertMsgForCartItems() {
    return showAlertForCartItems(this.cartDetails?.results, this.translate);
  }

  get isPaymentEnabledForOrg(): boolean {
    return checkPaymentEnabledForOrg(this.orgSettings);
  }

  get checkInsufficientPoint() {
    return this.cartDetails?.count &&
      this.cartDetails?.amount_payable > this.cartDetails?.user_points &&
      !this.isPaymentEnabledForOrg
      ? true
      : false;
  }

  get checkoutButtonDisabled() {
    return (
      (this.cartDetails && this.cartDetails?.count == 0) ||
      this.checkInsufficientPoint ||
      this.showAlertMsgForCartItems
    );
  }

  async onCheckout() {
    this.isShowCartPopup = false;
    if (
      !this.isPaymentEnabledForOrg &&
      this.cartDetails.amount_payable > this.cartDetails.user_points
    ) {
      showWarning(
        'msg.point.insufficient',
        this.toastr,
        this.translate,
        true,
        true,
      );
      return;
    }

    if (this.cartDetails?.all_voucher_cart === true) {
      const ref = this.dialog.open(OrderConfirmationWithCartComponent, {
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: { cartDetails: this.cartDetails, addressChoice: null },
      });
      const sub = ref.componentInstance.onCartDataChange.pipe(takeUntil(this.destroy$)).subscribe(
        async () => {
          await this.getCartDetails();
        });
    } else {
      const productReward = this.cartDetails?.results
        ?.filter(reward => reward.reward_details?.redemption_type === 'physical_product')
        .map(reward => reward.reward_details?.pk);

      const productRewardPk = productReward?.[0];
      this.router.navigate(['/reward', productRewardPk, 'address', 'select'], {
        queryParams: { qty: 1, cart: true },
      });
    }
  }

  gotoMyCart() {
    this.isShowCartPopup = false;
    if (this.router.url === '/my-cart') {
      this.store.dispatch(setSpinner({ spinner: true }));
      this.store.dispatch(setRefreshCart({ refreshCart: '1' }));
      setTimeout(() => {
        this.store.dispatch(setSpinner({ spinner: false }));
      }, 1000);
    }
    this.router.navigate(['/my-cart']);
  }

  onShowCartSummary(data: any) {
    this.store.dispatch(
      updateCartAll({
        cartDetails: this.cartDetails,
        cartItems: data?.cartItems,
      }),
    );
    this.isShowCartPopup = data?.count > 0;
  }

  async getCartDetails() {
    await this.api
      .getCartDetails()
      .then((res: Cart) => {
        if (res) {
          this.cartDetails = res;
          this.store.dispatch(
            updateCartAll({
              cartDetails: this.cartDetails,
              cartCount: res?.count,
            }),
          );
        }
      })
      .catch((err) => showError(err, this.toastr, this.translate))
      .finally(() => this.store.dispatch(setSpinner({ spinner: false })));
  }

  onClickedCart() {
    const matDrawerContent = document.querySelector('.page-content');
    if (this.isShowCartPopup) {
      this.renderer.addClass(matDrawerContent, 'mat-custom-content');
    } else {
      this.renderer.removeClass(matDrawerContent, 'mat-custom-content');
    }
  }
}
